import { fetch } from '@onviobr/fetch';
import { HttpError, lastValueFrom, JSON_REQUEST_HEADERS } from '@onviobr/helpers';
/**
 * Retrieves the Session and Bindings object
 * @public
 * @remarks This call will be cached once per browser session
 * @param session - The session manager used to retrieve the information
 * @returns Session and Bindings object
 */
export function getSessionAndBindingsV1(session) {
    return session.httpClient.getRaw('/api/security/v1/session-and-bindings', undefined, true);
}
/**
 * Retrieves the Session object
 * @internal
 * @param session - The session manager used to retrieve the information
 * @returns Session object
 */
export function getSessionV1(session) {
    return session.httpClient.getRaw('/api/security/v1/sessions');
}
/**
 * Retrieves the Contact ID from a {@link SessionAndBindingsV1}
 * @public
 * @param sessionAndBindings - The session and bindings object where to look for the Contact ID
 * @returns Contact ID
 */
export function getContactID(sessionAndBindings) {
    return sessionAndBindings.UdsSessionBindings.ApplicationConfiguration.ContactId;
}
/**
 * Retrieves the Company ID from a {@link SessionAndBindingsV1}
 * @public
 * @param sessionAndBindings - The session and bindings object where to look for the Company ID
 * @returns Company ID
 */
export function getCompanyID(sessionAndBindings) {
    return sessionAndBindings.UdsSessionBindings.ApplicationConfiguration.CompanyId;
}
/**
 * Retrieves the URL of a Vertical in the Routing Table from a {@link SessionAndBindingsV1}
 * @public
 * @param sessionAndBindings - The session and bindings object where to look for the Vertical URL
 * @param name - The name of the Vertical
 * @returns Vertical URL
 */
export function getVerticalRoute(sessionAndBindings, name) {
    var _a;
    return (_a = sessionAndBindings.UdsSessionBindings.RoutingTable[name]) !== null && _a !== void 0 ? _a : '';
}
/**
 * Retrieves the Infrastructure Access Control list from a {@link SessionAndBindingsV1}
 * @public
 * @param sessionAndBindings - The session and bindings object where to look for the Infrastructure Access Control list
 * @returns Infrastructure Access Control list
 */
export function getIACs(sessionAndBindings) {
    return sessionAndBindings.UdsSessionBindings.InfrastructureAccessControls;
}
/**
 * Retrieves the Feature Resource Permissions list from a {@link SessionAndBindingsV1}
 * @public
 * @param sessionAndBindings - The session and bindings object where to look for the Feature Resource Permissions list
 * @returns Feature Resource Permissions list
 */
export function getFRPsV1(sessionAndBindings) {
    return sessionAndBindings.UdsSessionBindings.FeatureResourcePermissions.FeatureResourcePermissionsMap;
}
/**
 * Retrieves the environment information
 * @public
 * @returns Environment information
 */
export async function getEnvironmentV1() {
    const response = await fetch('/api/auth/v1/environments', {
        method: 'GET',
        headers: JSON_REQUEST_HEADERS,
    });
    if (response.status >= 400) {
        throw new HttpError(response);
    }
    // This is by design to remove unwanted routing table that should come from the session
    const { locales, tokens, countryCode, primaryProduct, productDisplayName, systemFromEmailAddress } = await response.json();
    return { locales, tokens, countryCode, primaryProduct, productDisplayName, systemFromEmailAddress };
}
/**
 * Creates a JWT token from a UDS token
 * @public
 * @param uds - UDS token
 * @returns JWT token
 */
export async function createJWT(uds) {
    const response = await fetch('/api/security/v1/jwt/sessions', {
        method: 'POST',
        headers: {
            ...JSON_REQUEST_HEADERS,
            Authorization: `UDSLongToken ${uds}`,
        },
    });
    if (response.status >= 400) {
        throw new HttpError(response);
    }
    const body = await response.json();
    return body.token;
}
/**
 * Creates an Onvio session with a user and password
 * @public
 * @param uid - Username
 * @param pwd - Password
 * @returns Session tokens (UDS and JWT)
 */
export async function createSession(uid, pwd) {
    const response = await fetch('/api/auth/v2/login', {
        method: 'POST',
        body: JSON.stringify({
            uid,
            pwd,
            type: 'onvio',
            sp: 'onvio',
        }),
    });
    if (response.status >= 400) {
        throw new HttpError(response);
    }
    const body = await response.json();
    return { uds: body.uds, jwt: body.tokenValue };
}
/**
 * Creates an Onvio Session with a JWT token
 * @public
 * @param jwt - JWT token
 * @returns UDS token
 */
export async function createSessionWithJWT(jwt) {
    const response = await fetch('/api/security/v3/sessions/jwt', {
        method: 'POST',
        headers: {
            ...JSON_REQUEST_HEADERS,
            Authorization: `Bearer ${jwt}`,
        },
    });
    if (response.status >= 400) {
        throw new HttpError(response);
    }
    const body = await response.json();
    return body.LongToken;
}
/**
 * Creates an Onvio Session with a SSO token
 * @public
 * @param sso - SSO token
 * @returns UDS token
 */
export async function createSessionWithSSO(sso) {
    const response = await fetch(`/api/security/v1/sso?token=${sso}`, {
        method: 'GET',
        headers: JSON_REQUEST_HEADERS,
    });
    if (response.status >= 400) {
        throw new HttpError(response);
    }
    const body = await response.json();
    return body.LongToken;
}
/**
 * Retrieves an Onvio Session from a SSO token
 * @public
 * @param sso - SSO token
 * @returns UDS token
 */
export async function retrieveSessionWithSSO(sso) {
    const response = await fetch(`/api/security/v1/sso?pre-created=true&token=${sso}`, {
        method: 'GET',
        headers: JSON_REQUEST_HEADERS,
    });
    if (response.status >= 400) {
        throw new HttpError(response);
    }
    const body = await response.json();
    return body.LongToken;
}
/**
 * Creates a SSO token (single use token)
 * @public
 * @param session - Session used to create the SSO
 * @param vertical - Optional vertical to create a redirect URL
 * @param path - Optional path to create a redirect URL
 * @returns SSO token
 */
export async function createSSOToken(session, vertical, path) {
    var _a, _b;
    const body = await lastValueFrom(session.httpClient.postRaw('/api/security/v1/sso', {
        body: JSON.stringify({
            Vertical: vertical !== null && vertical !== void 0 ? vertical : 'Root',
            Path: path !== null && path !== void 0 ? path : '',
        }),
    }));
    const token = (_b = (_a = /token=(?<token>\w+)/gi.exec(body.Url)) === null || _a === void 0 ? void 0 : _a.groups) === null || _b === void 0 ? void 0 : _b.token;
    if (!token) {
        throw new Error(`The SSO token can not be extracted from the resulting URL.`);
    }
    if (typeof vertical === 'string') {
        return {
            token,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            url: body.Url,
        };
    }
    return {
        token,
    };
}
/**
 * Deletes an Onvio Session
 * @public
 * @param session - session to close
 */
export async function deleteSession(session) {
    await lastValueFrom(session.httpClient.deleteRaw('/api/security/v1/sessions'));
}
